import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Select } from '../Select/Select';
import { Tooltip } from '../Tooltip';
import * as styles from './styles.module.css';

/**
 * TimeRangeFilter Component
 * A reusable component for selecting time ranges with predefined or custom options
 * Matches Figma design: https://www.figma.com/design/DwhBVgd7QzzcwqWz8tzRnx/Reports?node-id=289-5941
 * 
 * @param {Object} value - Current time range value { mode, range, start, end, timezone }
 * @param {Function} onChange - Callback when time range changes
 * @param {boolean} showTimeZone - Whether to show timezone selector (default: true)
 */
function TimeRangeFilter({ 
  value = {
    mode: 'predefined', // 'predefined' or 'custom'
    range: 'last_7d',
    start: null,
    end: null,
    timezone: 'local', // 'host' or 'local' - default to local
  },
  onChange,
  showTimeZone = true,
}) {
  const { t } = useTranslation();
  
  const [mode, setMode] = useState(value.mode || 'predefined');
  const [selectedRange, setSelectedRange] = useState(value.range || 'last_7d');
  const [startDate, setStartDate] = useState(value.start ? value.start.split('T')[0] : '');
  const [startTime, setStartTime] = useState(value.start ? value.start.split('T')[1]?.substring(0, 5) : '00:00');
  const [endDate, setEndDate] = useState(value.end ? value.end.split('T')[0] : '');
  const [endTime, setEndTime] = useState(value.end ? value.end.split('T')[1]?.substring(0, 5) : '23:59');
  const [timezone, setTimezone] = useState(value.timezone || 'local');

  // Predefined range options
  const RANGE_OPTIONS = [
    { value: 'last_1h', label: t('timeRange.last1Hour') },
    { value: 'last_24h', label: t('timeRange.last24Hours') },
    { value: 'last_7d', label: t('timeRange.last7Days') },
    { value: 'last_30d', label: t('timeRange.last30Days') },
    { value: 'last_90d', label: t('timeRange.last90Days') },
    { value: 'last_365d', label: t('timeRange.last365Days') },
  ];

  // Parse datetime-local input into date and time
  useEffect(() => {
    if (value.start) {
      const [datePart, timePart] = value.start.split('T');
      if (datePart) setStartDate(datePart);
      if (timePart) setStartTime(timePart.substring(0, 5));
    }
    if (value.end) {
      const [datePart, timePart] = value.end.split('T');
      if (datePart) setEndDate(datePart);
      if (timePart) setEndTime(timePart.substring(0, 5));
    }
  }, [value.start, value.end]);

  // Handle mode change (Predefined vs Custom)
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (onChange) {
      onChange({
        mode: newMode,
        range: newMode === 'predefined' ? selectedRange : null,
        start: newMode === 'custom' ? startDate : null,
        end: newMode === 'custom' ? endDate : null,
        timezone,
      });
    }
  };

  // Handle predefined range change
  const handleRangeChange = (range) => {
    setSelectedRange(range);
    if (onChange) {
      onChange({
        mode: 'predefined',
        range,
        start: null,
        end: null,
        timezone,
      });
    }
  };

  // Handle custom date/time change
  const handleCustomDateChange = (field, dateValue, timeValue) => {
    let updatedStartDate = startDate;
    let updatedStartTime = startTime;
    let updatedEndDate = endDate;
    let updatedEndTime = endTime;

    if (field === 'start') {
      updatedStartDate = dateValue !== undefined ? dateValue : startDate;
      updatedStartTime = timeValue !== undefined ? timeValue : startTime;
      setStartDate(updatedStartDate);
      setStartTime(updatedStartTime);
    } else {
      updatedEndDate = dateValue !== undefined ? dateValue : endDate;
      updatedEndTime = timeValue !== undefined ? timeValue : endTime;
      setEndDate(updatedEndDate);
      setEndTime(updatedEndTime);
    }

    // Call onChange with current state
    if (onChange) {
      // Build the datetime strings if both date and time are present
      let newStart = null;
      let newEnd = null;

      // For start datetime - must have both date and time
      if (updatedStartDate && updatedStartTime) {
        newStart = `${updatedStartDate}T${updatedStartTime}`;
      }

      // For end datetime - must have both date and time
      if (updatedEndDate && updatedEndTime) {
        newEnd = `${updatedEndDate}T${updatedEndTime}`;
      }

      // Always call onChange to keep parent in sync, even if not complete
      onChange({
        mode: 'custom',
        range: null,
        start: newStart,
        end: newEnd,
        timezone,
      });
    }
  };

  // Handle timezone change
  const handleTimezoneChange = (newTimezone) => {
    setTimezone(newTimezone);
    if (onChange) {
      onChange({
        mode,
        range: mode === 'predefined' ? selectedRange : null,
        start: mode === 'custom' ? startDate : null,
        end: mode === 'custom' ? endDate : null,
        timezone: newTimezone,
      });
    }
  };

  return (
    <div className={styles.container}>
      {/* Predefined Range Section */}
      <div className={styles.section}>
        <label className={styles.radioLabel} htmlFor="predefined-range-radio">
          <input
            id="predefined-range-radio"
            type="radio"
            name="timeRangeMode"
            checked={mode === 'predefined'}
            onChange={() => handleModeChange('predefined')}
            className={styles.radioInput}
          />
          <span className={styles.radioText}>{t('timeRange.predefinedRange')}</span>
        </label>
        
        {mode === 'predefined' && (
          <div className={styles.dropdownContainer}>
            <Select
              value={selectedRange}
              onChange={handleRangeChange}
              options={RANGE_OPTIONS}
              className={styles.dropdown}
            />
          </div>
        )}
      </div>

      {/* Custom Range Section */}
      <div className={styles.section}>
        <label className={styles.radioLabel} htmlFor="custom-range-radio">
          <input
            id="custom-range-radio"
            type="radio"
            name="timeRangeMode"
            checked={mode === 'custom'}
            onChange={() => handleModeChange('custom')}
            className={styles.radioInput}
          />
          <span className={styles.radioText}>{t('timeRange.customRange')}</span>
        </label>

        {mode === 'custom' && (
          <div className={styles.customRangeInputs}>
            {/* Start Date/Time */}
            <div className={styles.dateTimeGroup}>
              <label htmlFor="start-date-input" className={styles.fieldLabel}>{t('timeRange.start')}</label>
              <div className={styles.dateTimeInputs}>
                <div className={styles.inputWrapper}>
                  <svg className={styles.inputIcon} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 1.5H2.5C1.94772 1.5 1.5 1.94772 1.5 2.5V9.5C1.5 10.0523 1.94772 10.5 2.5 10.5H9.5C10.0523 10.5 10.5 10.0523 10.5 9.5V2.5C10.5 1.94772 10.0523 1.5 9.5 1.5Z" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 0.5V2.5" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 0.5V2.5" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1.5 4.5H10.5" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="start-date-input"
                    type="date"
                    value={startDate}
                    onChange={(e) => handleCustomDateChange('start', e.target.value, undefined)}
                    className={styles.dateInput}
                    placeholder={t('timeRange.date')}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <svg className={styles.inputIcon} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 3V6L8 7" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => handleCustomDateChange('start', undefined, e.target.value)}
                    className={styles.timeInput}
                    placeholder={t('timeRange.time')}
                  />
                </div>
              </div>
            </div>

            {/* End Date/Time */}
            <div className={styles.dateTimeGroup}>
              <label htmlFor="end-date-input" className={styles.fieldLabel}>{t('timeRange.end')}</label>
              <div className={styles.dateTimeInputs}>
                <div className={styles.inputWrapper}>
                  <svg className={styles.inputIcon} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 1.5H2.5C1.94772 1.5 1.5 1.94772 1.5 2.5V9.5C1.5 10.0523 1.94772 10.5 2.5 10.5H9.5C10.0523 10.5 10.5 10.0523 10.5 9.5V2.5C10.5 1.94772 10.0523 1.5 9.5 1.5Z" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 0.5V2.5" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 0.5V2.5" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1.5 4.5H10.5" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="end-date-input"
                    type="date"
                    value={endDate}
                    onChange={(e) => handleCustomDateChange('end', e.target.value, undefined)}
                    className={styles.dateInput}
                    placeholder={t('timeRange.date')}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <svg className={styles.inputIcon} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 3V6L8 7" stroke="var(--color-text-secondary, #666666)" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => handleCustomDateChange('end', undefined, e.target.value)}
                    className={styles.timeInput}
                    placeholder={t('timeRange.time')}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Time Zone Section */}
      {showTimeZone && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('timeRange.timeZone')}</h3>
          <div className={styles.timezoneToggle}>
            <button
              type="button"
              className={`${styles.timezoneButton} ${timezone === 'local' ? styles.timezoneButtonActive : ''}`}
              onClick={() => handleTimezoneChange('local')}
            >
              {timezone === 'local' && (
                <svg className={styles.checkIcon} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {t('timeRange.localTime')}
            </button>
            <Tooltip
              content={t('timeRange.hostTimeZoneTooltip')}
              position="bottom"
              delay={0}
              className={styles.tooltipWrapper}
            >
              <button
                type="button"
                className={`${styles.timezoneButton} ${timezone === 'host' ? styles.timezoneButtonActive : ''}`}
                onClick={() => handleTimezoneChange('host')}
              >
                {timezone === 'host' && (
                  <svg className={styles.checkIcon} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {t('timeRange.hostTimeZone')}
              </button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
}

TimeRangeFilter.propTypes = {
  value: PropTypes.shape({
    mode: PropTypes.oneOf(['predefined', 'custom']),
    range: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
    timezone: PropTypes.oneOf(['host', 'local']),
  }),
  onChange: PropTypes.func.isRequired,
  showTimeZone: PropTypes.bool,
};

TimeRangeFilter.defaultProps = {
  value: {
    mode: 'predefined',
    range: 'last_7d',
    start: null,
    end: null,
    timezone: 'local',
  },
  showTimeZone: true,
};

export default TimeRangeFilter;

